import { getFAQList, patchAnswerFAQ } from "apis";
import { useAppModal } from "components/common/AppModalProvider";
import { useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery, useQueryClient } from "react-query";

const FAQManageComponent = () => {
    const queryClient = useQueryClient();
    const { showAlert, showConfirm } = useAppModal();
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [answerText, setAnswerText] = useState<string>("");
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);
    const itemsPerPage = 10;

    const { data, isLoading, isError } = useQuery(
        ["faqList", currentPage, itemsPerPage],
        () => getFAQList(currentPage, itemsPerPage),
        { keepPreviousData: true }
    );

    if (isLoading) return <div className="p-8"><div className="animate-pulse h-96 bg-achromatic-100 rounded-2xl" /></div>;
    if (isError) return <div className="p-8 text-red-500">오류가 발생했습니다.</div>;

    const faqList = data?.data.data.items || [];
    const totalItems = data?.data.data.total || 0;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    const handlePageClick = ({ selected }: { selected: number }) => setCurrentPage(selected + 1);
    const toggleRow = (id: string) => {
        if (expandedRow === id) {
            setExpandedRow(null);
            setAnswerText("");
        } else {
            setExpandedRow(id);
            const item = faqList.find((f: any) => f.id === id);
            setAnswerText(item?.answer || "");
        }
    };

    const handleAnswerSubmit = async (supportId: string) => {
        if (!answerText.trim()) {
            showAlert("답변 내용을 입력하세요.", { variant: 'error' });
            return;
        }
        if (!(await showConfirm('답변을 등록하시겠습니까?\n유저에게 알림이 전송됩니다.', {
            title: '답변 등록',
            confirmText: '등록',
            variant: 'info',
        }))) return;
        try {
            const response = await patchAnswerFAQ(supportId, answerText.trim());
            if (response.status === 200) {
                showAlert("답변이 등록되었습니다.", { variant: 'success' });
                setAnswerText("");
                setExpandedRow(null);
                queryClient.invalidateQueries(["faqList"]);
            }
        } catch (error) {
            showAlert("처리 중 오류가 발생했습니다.", { variant: 'error' });
        }
    };

    const getImageUrl = (imagePath: string) =>
        imagePath.startsWith('http') ? imagePath : `${process.env.REACT_APP_API_URL}/${imagePath}`;

    const handleDownload = async (imagePath: string) => {
        try {
            const response = await fetch(getImageUrl(imagePath));
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = imagePath.split('/').pop() || 'attachment';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch {
            showAlert("다운로드 중 오류가 발생했습니다.", { variant: 'error' });
        }
    };

    const AttachmentThumbnail = ({ imagePath, label }: { imagePath: string; label: string }) => (
        <div className="flex items-center gap-2">
            <div
                className="w-16 h-16 rounded-lg border border-achromatic-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary-300 transition-all"
                onClick={(e) => { e.stopPropagation(); setLightboxImage(imagePath); }}
            >
                <img
                    src={getImageUrl(imagePath)}
                    alt={label}
                    className="w-full h-full object-cover admin-downloadable"
                />
            </div>
            <div className="flex flex-col gap-1">
                <span className="text-[11px] text-achromatic-500">{label}</span>
                <button
                    className="text-[11px] text-primary-500 font-semibold hover:text-primary-600"
                    onClick={(e) => { e.stopPropagation(); handleDownload(imagePath); }}
                >
                    ⬇ 다운로드
                </button>
            </div>
        </div>
    );

    return (
        <div className="p-8">
            <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-achromatic-900">문의 관리</h1>
                <p className="text-sm text-achromatic-400 mt-1">전체 {totalItems}건의 고객 문의</p>
            </div>

            <div className="bg-white rounded-2xl border border-achromatic-100 overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-achromatic-100 bg-achromatic-50">
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">#</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">제목</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">이메일</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">첨부</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">상태</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">문의일</th>
                        </tr>
                    </thead>
                    <tbody>
                        {faqList.map((item: any, index: number) => (
                            <>
                                <tr
                                    key={item.id}
                                    className={`border-b border-achromatic-50 cursor-pointer transition-colors hover:bg-primary-50/30
                                        ${expandedRow === item.id ? 'bg-primary-50/20' : ''}`}
                                    onClick={() => toggleRow(item.id)}
                                >
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                    <td className="py-3 px-4 text-[13px] font-medium text-achromatic-800 truncate max-w-[200px]">{item.title}</td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500">{item.user?.email}</td>
                                    <td className="py-3 px-4 text-center">
                                        {(item.titleImage || item.contentImage) ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600">📎 있음</span>
                                        ) : (
                                            <span className="text-[11px] text-achromatic-300">-</span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold
                                            ${item.isAnswered ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                            {item.isAnswered ? '답변완료' : '미답변'}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">
                                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                                    </td>
                                </tr>

                                {expandedRow === item.id && (
                                    <tr key={`expanded-${item.id}`} className="bg-achromatic-50/50">
                                        <td colSpan={6} className="p-6">
                                            {/* 문의 내용 */}
                                            <div className="bg-white rounded-xl border border-achromatic-100 p-5 mb-4">
                                                <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-2">문의 내용</h3>
                                                <div className="text-[14px] font-semibold text-achromatic-900 mb-2">{item.title}</div>
                                                <div className="text-[13px] text-achromatic-600 leading-relaxed whitespace-pre-wrap">{item.content}</div>
                                            </div>

                                            {/* 첨부파일 썸네일 */}
                                            {(item.titleImage || item.contentImage) && (
                                                <div className="bg-white rounded-xl border border-achromatic-100 p-5 mb-4">
                                                    <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">첨부파일</h3>
                                                    <div className="flex flex-wrap gap-4">
                                                        {item.titleImage && <AttachmentThumbnail imagePath={item.titleImage} label="제목 이미지" />}
                                                        {item.contentImage && <AttachmentThumbnail imagePath={item.contentImage} label="내용 이미지" />}
                                                    </div>
                                                </div>
                                            )}

                                            {/* 고객 정보 */}
                                            <div className="bg-white rounded-xl border border-achromatic-100 p-5 mb-4">
                                                <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-2">고객 정보</h3>
                                                <div className="grid grid-cols-2 gap-1.5 text-[13px]">
                                                    <div className="flex"><span className="text-achromatic-400 w-20">닉네임</span><span className="font-medium text-achromatic-700">{item.user?.name}</span></div>
                                                    <div className="flex"><span className="text-achromatic-400 w-20">이메일</span><span className="font-medium text-achromatic-700">{item.user?.email}</span></div>
                                                </div>
                                            </div>

                                            {/* 기존 답변 표시 */}
                                            {item.isAnswered && item.answer && (
                                                <div className="bg-emerald-50/50 rounded-xl border border-emerald-100 p-5 mb-4">
                                                    <h3 className="text-[12px] font-bold text-emerald-600 uppercase tracking-wider mb-2">
                                                        등록된 답변 <span className="text-[11px] font-normal text-emerald-400 ml-2">
                                                            {item.answeredAt ? new Date(item.answeredAt).toLocaleString('ko-KR') : ''}
                                                        </span>
                                                    </h3>
                                                    <div className="text-[13px] text-achromatic-700 leading-relaxed whitespace-pre-wrap">{item.answer}</div>
                                                </div>
                                            )}

                                            {/* 답변 작성 영역 */}
                                            <div className="bg-white rounded-xl border border-achromatic-100 p-5 mb-4">
                                                <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-2">
                                                    {item.isAnswered ? '답변 수정' : '답변 작성'}
                                                </h3>
                                                <textarea
                                                    className="w-full p-3 border border-achromatic-200 rounded-lg text-[13px] text-achromatic-700 focus:outline-none focus:border-primary-400 resize-none"
                                                    rows={4}
                                                    placeholder="답변을 입력하세요..."
                                                    value={answerText}
                                                    onChange={(e) => setAnswerText(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>

                                            <div className="flex justify-end">
                                                <button
                                                    className="px-5 py-2 bg-primary-500 text-white text-[12px] font-bold rounded-lg hover:bg-primary-600 transition-colors"
                                                    onClick={(e) => { e.stopPropagation(); handleAnswerSubmit(item.id); }}
                                                >
                                                    {item.isAnswered ? '답변 수정' : '답변 등록'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </table>

                <div className="p-4 border-t border-achromatic-100 flex justify-center">
                    <ReactPaginate
                        previousLabel={"←"} nextLabel={"→"} breakLabel={"..."} pageCount={pageCount}
                        marginPagesDisplayed={1} pageRangeDisplayed={5} onPageChange={handlePageClick}
                        containerClassName={"flex items-center gap-1"}
                        previousClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        nextClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        pageClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-600 hover:bg-achromatic-100"}
                        activeClassName={"!bg-primary-500 !text-white"} activeLinkClassName={"text-white"}
                    />
                </div>
            </div>

            {/* Lightbox 모달 */}
            {lightboxImage && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
                    onClick={() => setLightboxImage(null)}
                >
                    <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getImageUrl(lightboxImage)}
                            alt="첨부파일 확대"
                            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg admin-downloadable"
                        />
                        <div className="flex items-center justify-center gap-3 mt-3">
                            <button
                                className="px-4 py-2 bg-white text-achromatic-700 text-[12px] font-bold rounded-lg hover:bg-achromatic-100 transition-colors"
                                onClick={() => handleDownload(lightboxImage)}
                            >
                                ⬇ 다운로드
                            </button>
                            <button
                                className="px-4 py-2 bg-achromatic-700 text-white text-[12px] font-bold rounded-lg hover:bg-achromatic-800 transition-colors"
                                onClick={() => setLightboxImage(null)}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FAQManageComponent;
